import { useState } from 'react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ConfirmationModal } from '@/components/ui/confirmation-modal';
import type { RevaluationItem } from '@/types/revaluation';
import { RotateCcw, Edit3 } from 'lucide-react';

interface RevaluationBatchGridProps {
  items: RevaluationItem[];
  onUpdateItem: (itemId: string, updates: Partial<RevaluationItem>) => void;
  readonly?: boolean;
  selectedItemIds?: Set<string>;
  onSelectionChange?: (selectedIds: Set<string>) => void;
  onReapplyDefaults?: (applyToAll: boolean) => void;
  revaluationMode: string;
  revaluationAmount: number;
}

export function RevaluationBatchGrid({ 
  items, 
  onUpdateItem, 
  readonly = false, 
  selectedItemIds = new Set(), 
  onSelectionChange,
  onReapplyDefaults
}: RevaluationBatchGridProps): React.ReactElement {
  const [showReapplyConfirm, setShowReapplyConfirm] = useState(false);
  const [reapplyMode, setReapplyMode] = useState<'all' | 'unmodified'>('all');

  const handleSelectAll = (checked: boolean) => {
    if (!onSelectionChange) return;
    if (checked) {
      onSelectionChange(new Set(items.map(item => item.id)));
    } else {
      onSelectionChange(new Set());
    }
  };

  const handleSelectItem = (itemId: string, checked: boolean) => {
    if (!onSelectionChange) return;
    const newSelection = new Set(selectedItemIds);
    if (checked) {
      newSelection.add(itemId);
    } else {
      newSelection.delete(itemId);
    }
    onSelectionChange(newSelection);
  };

  const handleRevaluedNBVChange = (item: RevaluationItem, newValue: string) => {
    const value = parseFloat(newValue);
    if (isNaN(value)) return;

    const adjustment = value - item.currentNBV;

    onUpdateItem(item.id, {
      revaluedNBV: value,
      revaluationAdjustment: adjustment,
      isOverridden: true,
    });
  };

  const handleResetToDefault = (itemId: string) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;

    onUpdateItem(itemId, {
      revaluedNBV: item.aiRevaluedNBV,
      revaluationAdjustment: item.aiRevaluedNBV - item.currentNBV,
      isOverridden: false,
    });
  };

  const handleReapplyDefaults = () => {
    setShowReapplyConfirm(true);
  };

  const confirmReapplyDefaults = () => {
    if (onReapplyDefaults) {
      onReapplyDefaults(reapplyMode === 'all');
    }
    setShowReapplyConfirm(false);
  };

  const formatCurrency = (value: number) => {
    return value.toFixed(2);
  };

  const overriddenCount = items.filter(item => item.isOverridden).length;

  return (
    <TooltipProvider>
      <div className="space-y-3">
        {!readonly && overriddenCount > 0 && (
          <div className="flex items-center justify-between bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg px-4 py-3">
            <div className="flex items-center gap-2">
              <Edit3 className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              <span className="text-sm text-amber-900 dark:text-amber-100">
                {overriddenCount} {overriddenCount === 1 ? 'line has' : 'lines have'} manual overrides
              </span>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleReapplyDefaults}
              className="border-amber-300 dark:border-amber-700 hover:bg-amber-100 dark:hover:bg-amber-900/30"
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reapply Defaults
            </Button>
          </div>
        )}

        <div className="border rounded-[10px] overflow-hidden bg-white dark:bg-black">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  {!readonly && onSelectionChange && (
                    <TableHead className="w-[40px]">
                      <Checkbox
                        checked={selectedItemIds.size === items.length && items.length > 0}
                        onCheckedChange={handleSelectAll}
                        aria-label="Select all"
                      />
                    </TableHead>
                  )}
                  <TableHead className="w-[120px]">Code</TableHead>
                  <TableHead className="min-w-[250px]">Description</TableHead>
                  <TableHead className="text-right w-[180px]">Net Book Value (£)</TableHead>
                  <TableHead className="text-right w-[200px]">
                    <div className="flex items-center justify-end gap-1">
                      Revalued Net Book Value (£)
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="cursor-help text-muted-foreground">ⓘ</span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="max-w-xs">
                          <p>Editable field. Manual changes will be marked as overrides.</p>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell 
                      colSpan={!readonly && onSelectionChange ? 5 : 4} 
                      className="text-center py-12 text-muted-foreground"
                    >
                      No assets added yet. Click "Add Assets" to begin.
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => (
                    <TableRow key={item.id}>
                      {!readonly && onSelectionChange && (
                        <TableCell className="w-[40px]">
                          <Checkbox
                            checked={selectedItemIds.has(item.id)}
                            onCheckedChange={(checked) => handleSelectItem(item.id, checked as boolean)}
                            aria-label={`Select ${item.assetCode}`}
                          />
                        </TableCell>
                      )}
                      <TableCell className="font-medium w-[120px]">{item.assetCode}</TableCell>
                      <TableCell className="min-w-[250px]">
                        <div className="flex items-center gap-2">
                          {item.assetDescription}
                          {item.isOverridden && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                                  <Edit3 className="h-3 w-3 mr-1" />
                                  Edited
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent side="top">
                                <p>User override - value differs from calculated default</p>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right w-[180px]">{formatCurrency(item.currentNBV)}</TableCell>
                      <TableCell className="w-[200px]">
                        {readonly ? (
                          <div className="text-right">{formatCurrency(item.revaluedNBV)}</div>
                        ) : (
                          <Input
                            type="number"
                            step="0.01"
                            value={item.revaluedNBV.toFixed(2)}
                            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => handleRevaluedNBVChange(item, e.target.value)}
                            className="text-right"
                            aria-describedby={item.isOverridden ? `override-${item.id}` : undefined}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      <ConfirmationModal
        open={showReapplyConfirm}
        onOpenChange={setShowReapplyConfirm}
        onConfirm={confirmReapplyDefaults}
        title="Reapply Defaults"
        description={
          <div className="space-y-4">
            <p>This will recalculate default values from the header settings.</p>
            <div className="space-y-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="reapply-mode"
                  checked={reapplyMode === 'all'}
                  onChange={() => setReapplyMode('all')}
                  className="h-4 w-4"
                />
                <span className="text-sm">Apply to all lines (overwrite manual values)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="reapply-mode"
                  checked={reapplyMode === 'unmodified'}
                  onChange={() => setReapplyMode('unmodified')}
                  className="h-4 w-4"
                />
                <span className="text-sm">Apply only to unmodified lines (keep manual values)</span>
              </label>
            </div>
          </div>
        }
        confirmText="Apply"
      />
    </TooltipProvider>
  );
}
